import Vue from 'vue';
import VueI18n from 'vue-i18n';
import es from './locales/es';
import en from './locales/en';
import ca from './locales/ca';

Vue.use(VueI18n);

const SUPPORTED = ['es', 'en', 'ca'] as const;
type Locale = (typeof SUPPORTED)[number];

function isSupported(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED as readonly string[]).includes(value);
}

/**
 * Resolve the initial locale.
 *   1. ?lang=xx query param
 *   2. localStorage('locale')
 *   3. navigator.language base subtag
 *   4. 'es' fallback
 */
function resolveInitialLocale(): Locale {
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get('lang');
    if (isSupported(fromQuery)) {
      localStorage.setItem('locale', fromQuery);
      return fromQuery;
    }
  } catch {
    // ignore
  }

  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('locale') : null;
  if (isSupported(stored)) return stored;

  if (typeof navigator !== 'undefined' && navigator.language) {
    const base = navigator.language.toLowerCase().split('-')[0];
    if (isSupported(base)) {
      localStorage.setItem('locale', base);
      return base;
    }
  }

  return 'es';
}

const i18n = new VueI18n({
  locale: resolveInitialLocale(),
  fallbackLocale: 'es',
  messages: { es, en, ca },
  silentTranslationWarn: process.env.NODE_ENV === 'production',
});

export const SUPPORTED_LOCALES = SUPPORTED;
export type AppLocale = Locale;

export function setLocale(next: Locale): void {
  i18n.locale = next;
  try {
    localStorage.setItem('locale', next);
  } catch {
    /* ignore */
  }
}

export default i18n;
